/**
 * Per-widget time-period selector model. Service functions across the
 * dashboard layer accept a `days` integer; this type wraps that with a
 * named key for UI display and an optional explicit date range when the
 * user picks "Custom".
 */
export type TimePeriodKey = 'today' | '7d' | '30d' | '90d' | 'custom';

export interface TimePeriod {
  key: TimePeriodKey;
  days: number;
  customFrom?: string;
  customTo?: string;
}

export const TIME_PERIOD_LABEL: Record<TimePeriodKey, string> = {
  today: 'Today',
  '7d': 'Last 7 Days',
  '30d': 'Last 30 Days',
  '90d': 'Last 90 Days',
  custom: 'Custom Range',
};

export const TIME_PERIOD_TODAY: TimePeriod = { key: 'today', days: 1 };
export const TIME_PERIOD_7D: TimePeriod = { key: '7d', days: 7 };
export const TIME_PERIOD_30D: TimePeriod = { key: '30d', days: 30 };
export const TIME_PERIOD_90D: TimePeriod = { key: '90d', days: 90 };

export function timePeriodFromKey(
  key: TimePeriodKey,
  prev: TimePeriod | null = null,
): TimePeriod {
  switch (key) {
    case 'today': return TIME_PERIOD_TODAY;
    case '7d': return TIME_PERIOD_7D;
    case '30d': return TIME_PERIOD_30D;
    case '90d': return TIME_PERIOD_90D;
    case 'custom':
      return {
        key: 'custom',
        days: prev?.key === 'custom' ? prev.days : 30,
        customFrom: prev?.customFrom,
        customTo: prev?.customTo,
      };
  }
}

// Inclusive day count between two YYYY-MM-DD strings. Returns null when the
// range is missing or invalid; callers fall back to the prior period.
export function daysFromCustomRange(from?: string, to?: string): number | null {
  if (!from || !to) return null;
  const fromMs = Date.parse(from);
  const toMs = Date.parse(to);
  if (isNaN(fromMs) || isNaN(toMs) || toMs < fromMs) return null;
  const days = Math.round((toMs - fromMs) / (1000 * 60 * 60 * 24)) + 1;
  return Math.max(1, days);
}
